<template>
  <Teleport to="body">
    <!-- <div v-if="isOpen" class="modal-overlay" @click="handleOverlayClick">
      <div
        class="modal-container"
        :class="{
          'modal-enter': isEntering,
          'modal-exit': isExiting,
          'no-animation': suppressAnimation,
        }"
        :style="{ transform: swipeTransform }"
        @click.stop
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
      >
        <div class="drag-indicator"></div>
        <div class="modal-header">
          <div></div>
          <h2>{{ $t('nav.userMenu') }}</h2>
          <button @click="closeModal" class="close-button">
            <IconRenderer
              size="medium"
              :autoplay="false"
              :loop="false"
              name="close-outline"
              color="var(--ion-icon-color)"
            />
          </button>
        </div>

        <div class="navigation-grid">
          <template v-for="route in userRoutes" :key="route.name">
            <div class="nav-item" @click="navigateTo(route.name as string)">
              <IconRenderer
                v-if="route.meta?.icon"
                size="medium"
                color="var(--ion-icon-color)"
                :autoplay="false"
                :loop="false"
                :name="route.meta.icon as string"
              />
              <span class="nav-label">{{
                route.meta?.title ? $t(route.meta.title as string) : route.name
              }}</span>
            </div>
          </template>
        </div>
      </div>
    </div> -->
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted, onUnmounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAppRouter } from '@/router/composables/useAppRouter';
import IconRenderer from '@/common/components/ui/icon/IconRenderer.vue';

const { navigateWithRoleCheck, getMenuRoutesByRoles } = useAppRouter();

const userRoutes = computed(() => getMenuRoutesByRoles());
interface Props {
  isOpen: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  close: [];
}>();

const router = useRouter();
const route = useRoute();
const suppressAnimation = ref(false);
const isNavigating = ref(false);
const isEntering = ref(false);
const isExiting = ref(false);

// Swipe handling
const swipeTransform = ref('');
const touchStartY = ref(0);
const touchCurrentY = ref(0);
const isDragging = ref(false);
const swipeThreshold = 100; // pixels to trigger close
const velocityThreshold = 0.5; // pixels per ms

// Watch for modal open/close to trigger animations
watch(
  () => props.isOpen,
  (newVal, oldVal) => {
    if (newVal && !oldVal && !isNavigating.value) {
      // Modal opening
      isEntering.value = true;
      isExiting.value = false;
      setTimeout(() => {
        isEntering.value = false;
      }, 300);
    }
  }
);

// Watch for route changes to suppress animation during navigation
watch(
  () => route.path,
  (newPath, oldPath) => {
    if (props.isOpen && oldPath && newPath !== oldPath && isNavigating.value) {
      suppressAnimation.value = true;
      // Reset after a short delay
      setTimeout(() => {
        suppressAnimation.value = false;
        isNavigating.value = false;
      }, 100);
    }
  }
);

const navigateTo = (path: string) => {
  isNavigating.value = true;
  suppressAnimation.value = true;

  if (route.path === path) {
    router.replace('/').then(() => {
      navigateWithRoleCheck(path);
    });
  } else {
    navigateWithRoleCheck(path);
  }
};

const closeModal = () => {
  // Reset swipe transform when closing
  swipeTransform.value = '';
  isEntering.value = false;
  isExiting.value = true;
  isNavigating.value = false;
  suppressAnimation.value = false;
  // Wait for exit animation to complete before emitting close
  nextTick(() => {
    setTimeout(() => {
      emit('close');
    }, 300); // Match the animation duration
  });
  // Always emit close immediately, let parent handle the state
};

const handleOverlayClick = () => {
  closeModal();
};

// Handle escape key
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.isOpen) {
    closeModal();
  }
};

const handleTouchStart = (event: TouchEvent) => {
  if (event.touches.length !== 1) return;

  const touch = event.touches[0];
  touchStartY.value = touch.clientY;
  touchCurrentY.value = touch.clientY;
  isDragging.value = true;
  swipeTransform.value = '';
};

const handleTouchMove = (event: TouchEvent) => {
  if (!isDragging.value || event.touches.length !== 1) return;

  event.preventDefault();
  const touch = event.touches[0];
  touchCurrentY.value = touch.clientY;

  const deltaY = touchCurrentY.value - touchStartY.value;

  // Only allow downward swipes
  if (deltaY > 0) {
    const translateY = Math.min(deltaY, window.innerHeight * 0.5);
    swipeTransform.value = `translateY(${translateY}px)`;
  }
};

const handleTouchEnd = (event: TouchEvent) => {
  if (!isDragging.value) return;

  const deltaY = touchCurrentY.value - touchStartY.value;
  const velocity = deltaY / (Date.now() - (event.timeStamp - 100)); // rough velocity calculation

  // Close modal if swipe distance or velocity threshold is met
  if (deltaY > swipeThreshold || (deltaY > 50 && velocity > velocityThreshold)) {
    closeModal();
  } else {
    // Reset position with animation
    swipeTransform.value = '';
  }

  isDragging.value = false;
  touchStartY.value = 0;
  touchCurrentY.value = 0;
};

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 10000;
  padding: 0;
}

.modal-container {
  background: var(--ion-background-color-primary);
  width: 100%;
  max-width: 100%;
  border-radius: 8px 8px 0 0;
  box-shadow: var(--ion-box-shadow-hover);
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transform: translateY(0);
  transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  user-select: none;
  -webkit-user-select: none;
  padding-bottom: 30px;
}

/* Animation classes */
.modal-container.modal-enter {
  animation: slideUp 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
}

.modal-container.modal-exit {
  animation: slideDown 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
}

.modal-container.no-animation {
  transform: translateY(0) !important;
  animation: none !important;
  transition: none !important;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

@keyframes slideDown {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(100%);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1px 20px 8px;
  background: var(--ion-background-color-primary);
}

.modal-header h2 {
  font-size: 18px;
  font-weight: 600;
  color: var(--ion-text-color);
}

.close-button {
  background: none;
  cursor: pointer;
  color: var(--ion-icon-primary);
  transition: background-color 0.2s ease;
}

.close-button:hover {
  background: var(--ion-color-light, #f0f0f0);
}

.close-button ion-icon {
  font-size: 30px;
}

.drag-indicator {
  width: 36px;
  height: 4px;
  background: var(--ion-color-medium, #666);
  border-radius: 2px;
  margin: 8px auto;
  opacity: 0.6;
}

/* Grid Navigation - 4 colonnes par ligne */
.navigation-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  padding: 0px 16px 40px 16px;
  background: var(--ion-background-color-primary);
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 4px;
  background: transparent;
  border-radius: 3vmin;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    transform 0.1s ease;
  text-align: center;
  border: none;
}

.nav-item:hover {
  background: var(--ion-color-light, #f0f0f0);
}

.nav-item:active {
  background: var(--ion-color-medium, #e0e0e0);
  transform: scale(0.95);
}

/* Labels */
.nav-label {
  /*  */
  font-size: 12px;
  color: var(--ion-text-color);
  font-weight: 500;
  text-align: center;
  line-height: 1.2;
  word-wrap: break-word;
  hyphens: auto;
  max-width: 100%;
}
</style>
